<script setup lang="ts">
import MiniMap from "~/components/pages/catalog/MiniMap.vue";
import type { WithContext, BreadcrumbList, ItemList, WebPage, Person, Organization } from 'schema-dts';
import { useJsonLdSchema } from '~/composables/useJsonLd';

const route = useRoute();
const config = useRuntimeConfig()

const globalStore = useGlobalStore();
const userStore = useUserStore();

const specificationId = computed(() => {
    if(globalStore.specifications.find(item => item.documentId === route.params.slug)){
        return route.params.slug
    }
    
    const foundSpecification = globalStore.specifications.find(item => item.slug === route.params.slug)?.documentId
    if (!foundSpecification) {
        throw createError({
            statusCode: 404,
            message: 'Specification not found'
        })
    }
    return foundSpecification
})

const {
    isLoaded,
    filterForm,
    mastersList,
    total,
    filtersList,
    currentSpecification,
    parentSpecification,
    specificationChildList,
    cleanedFilterForm,
    getFilters,
    getMasters
} = usePortfolio(specificationId.value as string);

await getFilters()
await getMasters()

watch(filterForm, async (value) => {
    await getMasters()
}, {
    deep: true
})

useSeoMeta({
    robots: 'index, follow',
    title: currentSpecification.value?.name,
    description: () => currentSpecification.value?.description,
    ogImage: currentSpecification.value?.thumbnail?.url,
    ogImageType: 'image/jpeg',
    ogType: 'website',
    ogUrl: () => config.public.frontUrl + route.path,
    ogTitle: () => currentSpecification.value?.name,
    ogDescription: () => currentSpecification.value?.description,
    twitterTitle: () => currentSpecification.value?.name,
    twitterDescription: () => currentSpecification.value?.description,
    twitterImage: currentSpecification.value?.thumbnail?.url,
    twitterImageType: 'image/jpeg',
})

useHead({
    link: [
        {
            rel: 'canonical',
            href: `${config.public.frontUrl}${currentSpecification.value?.slug ? '/catalog/category/' + currentSpecification.value.slug : route.path}`,
        },
    ],
})

const { jsonLd } = useJsonLdSchema({
    breadcrumbs: computed(() => {
        const items = [];

        if (parentSpecification.value) {
            items.push({
                name: parentSpecification.value.name,
                url: `${config.public.frontUrl}/catalog/category/${parentSpecification.value.slug}`
            });
        }

        if (currentSpecification.value) {
            items.push({
                name: currentSpecification.value.name,
                url: `${config.public.frontUrl}/catalog/category/${currentSpecification.value.slug}`
            });
        }

        return items;
    }),
    items: computed(() => mastersList.value?.map(master => ({
        name: master.user.username || '',
        url: `${config.public.frontUrl}/catalog/portfolio/${master.documentId}`,
        description: currentSpecification.value?.description || '',
        image: master.user.avatar?.url || '',
        jobTitle: currentSpecification.value?.name || ''
    })) || []),
    pageData: computed(() => ({
        name: currentSpecification.value?.name || 'Master\'s catalog',
        description: currentSpecification.value?.description || 'Browse catalog of professionals',
        url: `${config.public.frontUrl}${route.path}`
    })),
    homeUrl: config.public.frontUrl
});

useJsonld(() => jsonLd.value);

// Исправляем типы для фильтров
const reviewsOptions = computed(() => filtersList.value?.reviews?.map(review => ({
    name: review.label,
    documentId: review.value.toString(),
    value: review.value
})) || []);

</script>

<template>
    <div class="grid gap-12 lg:grid-cols-4">

        <div class="aside hidden lg:block">
            <div class="page_title">
                <div class="breadcrumb mb-4">
                    <NuxtLink 
                        to="/"
                        class="group text-md text-gray-400 hover:text-gray-600 transition-all">
                        {{ $t('Home') }}
                        <Icon name="ri:arrow-drop-right-line" size="24px" class="ml-0 transition-all group-hover:ml-1 rtl:rotate-180" />
                    </NuxtLink>
                </div>
                <p class="text-3xl font-bold mb-7">{{ currentSpecification?.name }}</p>
            </div>

            <ul class="nav">
                <li v-if="parentSpecification" class="my-1">
                    <NuxtLink 
                        :to="{name: 'master_category', params: {slug: parentSpecification.slug}}"
                        class="list-container block w-full py-2 px-3 transition-all hover:pl-4 rtl:hover:pr-4"
                        :class="{ list_container_active: parentSpecification.documentId == filterForm.specificationId[0] }">
                        {{ parentSpecification.name }}
                    </NuxtLink>
                </li>
                <li class="my-1 pl-4" v-for="item in specificationChildList">
                    <NuxtLink 
                        :to="{name: 'master_category', params: {slug: item.slug}}"
                        class="list-container block w-full py-2 px-3 transition-all hover:pl-4 rtl:hover:pr-4"
                        :class="{ list_container_active: item.documentId == filterForm.specificationId[0] }">
                        {{ item.name }}
                    </NuxtLink>
                </li>
            </ul>
            
        </div>

        <div class="col-span-1 lg:col-span-3">
            <!-- <client-only>
                <MiniMap :filters="cleanedFilterForm" :specification-id="filterForm.specificationId[0]"/>
            </client-only> -->
            <div class="mobile_triggers flex flex-wrap gap-4 md:hidden mb-6">
                <CustomUiFiltersModal>
                    <template #trigger>
                        <UiButton variant="lighter" class="text-gray-500">
                            <Icon size="15" class="mr-1" name="ri:filter-3-line"/>
                            <span class="text-sm">{{ $t("Filters") }}</span>
                        </UiButton>
                    </template>
                    <div class="px-5">
                        <CustomUiBtnFilterLabels
                            v-if="filtersList?.cities?.length"
                            v-model="filterForm.city"
                            :label="$t('Location')"
                            :options="filtersList?.cities"
                            class="mb-8"
                        />
                        <CustomUiBtnFilterRangeInline
                            v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to"
                            v-model:minValue="filterForm.priceFrom"
                            v-model:maxValue="filterForm.priceTo"
                            :min="filtersList.price.from"
                            :max="filtersList.price.to"
                            :label="$t('Price range')"
                            class="mb-8"
                        />
                        <!-- <CustomUiBtnFilterLabelSingle
                            v-if="filtersList?.reviews?.length"
                            v-model="filterForm.reviewsCount"
                            :label="$t('Reviews')"
                            :options="reviewsOptions"
                            option-label="name"
                            option-value="value"
                            class="mb-8"
                        /> -->
                        
                        <CustomUiBtnFilterLabels
                            v-if="filtersList?.employments?.length"
                            v-model="filterForm.employmentType"
                            :label="$t('Employment')"
                            :options="filtersList.employments"
                            class="mb-8"
                        />
                        <CustomUiBtnFilterLabels
                            v-if="filtersList?.educations?.length"
                            v-model="filterForm.educationType"
                            :label="$t('Education')"
                            :options="filtersList.educations"
                            class="mb-8"
                        />
                    </div>
                </CustomUiFiltersModal>
                <CustomUiCategoriesModal v-if="!route.path.includes('search')" type="masters" >
                    <template #trigger>
                        <UiButton variant="lighter" class="text-gray-500">
                            <Icon size="15" class="mr-1" name="ri:menu-search-line"/>
                            <span class="text-sm">{{ $t("Category") }}</span>
                        </UiButton>
                    </template>
                </CustomUiCategoriesModal>
            </div>
            <div v-if="filtersList" class="category_filters hidden md:flex flex-wrap gap-2 mb-8">
                    <CustomUiBtnFilterCheckbox
                        v-if="filtersList?.cities?.length"
                        v-model="filterForm.city"
                        :label="$t('Location')"
                        :options="filtersList?.cities"
                        icon="ri:user-location-line"
                        btn_class="max-h-[36px] px-2 w-full"
                        class="" />
                
                    <CustomUiBtnFilterRange
                        v-if="filtersList?.price && filtersList?.price?.from !== null && filtersList?.price?.to"
                        v-model:minValue="filterForm.priceFrom"
                        v-model:maxValue="filterForm.priceTo"
                        :min="filtersList.price.from"
                        :max="filtersList.price.to"
                        :label="$t('Price range')"
                        icon="ri:coins-line"
                        btn_class="max-h-[36px] px-2 w-full"
                        class="col-span-1" />
<!--                     <CustomUiBtnFilterCheckbox v-if="filtersList?.tags.length" v-model="filterForm.reviews" :label="$t('Reviews')" icon="ri:chat-heart-line" :options="filtersList.tags" btn_class="max-h-[36px] px-2 w-full" class="col-span-1" /> -->
                    <!-- <CustomUiBtnFilterSelect
                        v-if="filtersList?.reviews?.length"
                        v-model="filterForm.reviewsCount"
                        :label="$t('Reviews')"
                        :options="filtersList.reviews"
                        icon="ri:chat-heart-line"
                        btn_class="max-h-[36px] px-2 w-full"
                        class="col-span-1" /> -->
                    <CustomUiBtnFilterCheckbox
                        v-if="filtersList?.employments?.length"
                        v-model="filterForm.employmentType"
                        :label="$t('Employment')"
                        :options="filtersList.employments"
                        icon="ri:briefcase-2-fill"
                        btn_class="max-h-[36px]
                        px-2 w-full"
                        class="" />
                    <CustomUiBtnFilterCheckbox
                        v-if="filtersList?.educations?.length"
                        v-model="filterForm.educationType"
                        :label="$t('Education')"
                        :options="filtersList.educations"
                        icon="ri:book-line"
                        btn_class="max-h-[36px] px-2 w-full"
                        class="" />
                </div>
            <template v-if="isLoaded">
                <div v-if="mastersList?.length" class="grid grid-cols-1 gap-8" >
                    <PagesCatalogMasterCard v-for="item in mastersList" :item="item" :current_specification="filterForm.specificationId[0]" :parent_specification="parentSpecification?.documentId" />
                    <CustomUiPagination v-if="mastersList.length < total" @update:page="(page) => filterForm.page = page" :total="total" :per_page="filterForm.pageSize" :page="filterForm.page" class="mx-auto" />
                </div>

                <div v-else class="h-full flex flex-col items-center py-10" >
                    <img src="/img/EmptyStates.png" class="mb-4">
                    <p class="ys-home-custom-text text-center mb-8">
                        {{ $t("There are no specialists in this category yet") }}
                    </p>
                    <UiButton variant="outline" v-if="userStore.isAuthenticated" as-child class="text-md py-6 px-6 border-primary-500 rounded-lg font-medium text-primary-500 hover:bg-primary-50 hover:text-primary-500 hover:border-transparent">
                        <NuxtLink to="/customer-office/profile/">
                            <Icon size="20" class="mr-1" name="ri:user-line" />
                            {{ $t("Become a specialist") }}
                        </NuxtLink>
                    </UiButton>
                </div>
            </template>
            <template v-else>
                <div class="grid grid-cols-1 gap-8">
                    <UiSkeleton class="rounded-xl h-60" v-for="i in 5" />
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.list-container {
    transition: padding 0.5s ease;
    color: rgba(10, 13, 20, 1);

    &:hover {
        border-radius: 8px;
        background: #f6f8fa;
    }
}

.list_container_active {
    font-weight: 600;
    border-radius: 8px;
    background: rgba(246, 248, 250, 1);
    transition: 0.5s ease;
}

.ys-home-custom-text {
    font-family: Involve, sans-serif;
    font-style: italic;
    color: rgba(58, 62, 89, 1);
    font-size: 39px;
    font-weight: 500;
    line-height: 45px;
    @media(max-width:768px){
        font-size: 21px;
        line-height: 30px;
    }
}
</style>